/**
 * Accesso con Equal PSW (single sign-on dal password manager aziendale).
 *
 * Flusso: il login genera uno `state` casuale, lo salva in localStorage (10 minuti) e manda il browser a
 * `{vault}/sso/authorize?app=crm.equalstudio.it&state=…`; il vault rimanda a `/sso?assertion=…&state=…`
 * (percorso registrato nel vault: non cambiarlo), dove `SsoPage` controlla lo state e manda
 * l'asserzione a `POST /api/auth/sso`.
 *
 * Rinvio automatico: chi apre il login senza sessione va al vault da solo, ma una volta sola per
 * sessione del browser (sessionStorage), mai con `?error=` e mai dopo un logout esplicito
 * (localStorage, finché non si rientra) — così non c'è modo di girare in tondo.
 */

export const EQUAL_PSW_URL: string =
  (import.meta.env['VITE_EQUALPSW_URL'] as string | undefined)?.replace(/\/+$/, '') || 'https://psw.equalstudio.it'
export const EQUAL_PSW_APP: string =
  (import.meta.env['VITE_EQUALPSW_APP'] as string | undefined) || 'crm.equalstudio.it'

const STATE_KEY = 'crm.sso.state'
const AUTO_TRIED_KEY = 'crm.sso.autoTried'
const LOGGED_OUT_KEY = 'crm.sso.loggedOut'

export const SSO_ERROR_MESSAGES: Record<string, string> = {
  sso_invalid: 'L’accesso con Equal PSW non è valido o è scaduto. Riprova con il pulsante qui sotto.',
  sso_no_user: 'Il tuo account Equal PSW non corrisponde a nessun utente attivo del CRM. Chiedi a un amministratore di abilitarti, oppure accedi con email e password.',
  sso_unavailable: 'Equal PSW non è raggiungibile in questo momento. Riprova tra poco oppure accedi con email e password.',
  sso_state: 'La richiesta di accesso con Equal PSW non è partita da questa finestra o è scaduta. Riprova con il pulsante qui sotto.',
  sso_missing: 'Equal PSW non ha restituito un accesso valido. Riprova con il pulsante qui sotto.',
  RATE_LIMITED: 'Troppi tentativi di accesso. Riprova tra qualche minuto.',
}

export function ssoErrorMessage(code: string): string {
  return SSO_ERROR_MESSAGES[code] ?? 'Accesso con Equal PSW non riuscito. Riprova oppure accedi con email e password.'
}

function safeGet(store: () => Storage, key: string): string | null {
  try { return store().getItem(key) } catch { return null }
}
function safeSet(store: () => Storage, key: string, value: string): void {
  try { store().setItem(key, value) } catch { /* storage non disponibile */ }
}
function safeRemove(store: () => Storage, key: string): void {
  try { store().removeItem(key) } catch { /* storage non disponibile */ }
}
const session = () => sessionStorage
const local = () => localStorage

export function newState(): string {
  const bytes = new Uint8Array(32)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
}

export function authorizeUrl(state: string): string {
  const params = new URLSearchParams({ app: EQUAL_PSW_APP, state })
  return `${EQUAL_PSW_URL}/sso/authorize?${params.toString()}`
}

const STATE_TTL_MS = 10 * 60 * 1000

/**
 * Genera e salva lo state, poi va al vault. In localStorage e non in sessionStorage: su Android il
 * ritorno dal vault (o dall'app Equal PSW) arriva spesso in un'altra scheda o nel browser invece
 * che nella PWA, che condividono localStorage ma non sessionStorage. Scade dopo 10 minuti.
 */
export function startEqualPswLogin(navigate: (url: string) => void = url => window.location.assign(url)): void {
  const state = newState()
  safeSet(local, STATE_KEY, JSON.stringify({ state, exp: Date.now() + STATE_TTL_MS }))
  navigate(authorizeUrl(state))
}

function savedState(): string | null {
  const raw = safeGet(local, STATE_KEY)
  if (!raw) return null
  try {
    const v = JSON.parse(raw) as { state?: unknown; exp?: unknown }
    return typeof v.state === 'string' && typeof v.exp === 'number' && v.exp > Date.now() ? v.state : null
  } catch { return null }
}

/**
 * Controlla lo state di ritorno contro quello salvato e lo consuma (vale una volta sola). Senza
 * state salvato (scaduto, o richiesta non partita da questo browser) rifiuta.
 */
export function consumeState(returned: string | null): boolean {
  const saved = savedState()
  safeRemove(local, STATE_KEY)
  if (!saved || !returned || saved.length !== returned.length) return false
  let diff = 0
  for (let i = 0; i < saved.length; i++) diff |= saved.charCodeAt(i) ^ returned.charCodeAt(i)
  return diff === 0
}

/** Il rinvio automatico al vault è permesso? (vedi il commento in cima) */
export function shouldAutoRedirect(search: string): boolean {
  const params = new URLSearchParams(search)
  if (params.has('error') || params.has('logout')) return false
  if (safeGet(local, LOGGED_OUT_KEY) === '1') return false
  if (safeGet(session, AUTO_TRIED_KEY) === '1') return false
  return true
}

export function markAutoRedirectTried(): void {
  safeSet(session, AUTO_TRIED_KEY, '1')
}

/** Logout esplicito: niente rinvio automatico finché l'utente non rientra. */
export function markLoggedOut(): void {
  safeSet(local, LOGGED_OUT_KEY, '1')
  safeSet(session, AUTO_TRIED_KEY, '1')
}

/** Accesso riuscito (qualunque metodo): il prossimo login senza sessione può di nuovo andare al vault da solo. */
export function clearLoggedOut(): void {
  safeRemove(local, LOGGED_OUT_KEY)
}
